/**
 * A small library of named credit curves: tight, standard and loose, for onset and for release.
 * It is a preset-authoring convenience over the one curve mechanism, not new machinery: every
 * entry is an ordinary {@link CreditCurve}, plain data, and a preset may use one as it is or write
 * its own.
 *
 * "Not judged" is not a curve. To stop judging a dimension, leave it out of the note judgment;
 * an excluded dimension never pads the combiner, where a flat "do not care" curve would.
 *
 * The values are starting values. Standard onset is the shared default's onset window; standard
 * release is the shipping web client's release window. Tight halves each distance, loose takes
 * one and a half times it. Replaying recorded sessions is what settles them.
 */

import { CreditCurve, CurvePoint } from './creditCurve';

export type Tightness = 'tight' | 'standard' | 'loose';

export const TIGHTNESS: readonly Tightness[] = ['tight', 'standard', 'loose'];

/** 0.7 just past the plateau, then nothing once `width` past it. */
function yellowBand(width: number): CurvePoint[] {
  return [
    { distance: 0, credit: 0.7, shape: 'step' },
    { distance: width, credit: 0, shape: 'step' },
  ];
}

/** 0.7 just past the plateau, however far: a mistimed release is never red. */
const YELLOW_ONWARD: readonly CurvePoint[] = [{ distance: 0, credit: 0.7, shape: 'step' }];

/** Onset windows, in beats: full credit around the written onset, a yellow band, then red. */
export const ONSET_CURVES: Readonly<Record<Tightness, CreditCurve>> = {
  tight: {
    unit: 'beats',
    plateau: [-0.1, 0.1],
    below: yellowBand(0.075),
    above: yellowBand(0.2),
  },
  standard: {
    unit: 'beats',
    plateau: [-0.2, 0.2],
    below: yellowBand(0.15),
    above: yellowBand(0.4),
  },
  loose: {
    unit: 'beats',
    plateau: [-0.3, 0.3],
    below: yellowBand(0.225),
    above: yellowBand(0.6),
  },
};

/**
 * Release windows, for the offset dimension. Each side has its own unit. Early, the green zone
 * opens a share of the note before its written end; late, it closes a fixed time after it.
 * Outside it a release is yellow, never red.
 *
 * Standard is the shipping release window exactly. The key must be held at least half of a note
 * up to a quarter; between a quarter and a half note the hold grows on an eased curve (exponent
 * 0.6) from half a beat to 1.25 beats; from a half note on, the green zone opens 0.75 beat before
 * the end. It closes 0.3 beat after the end. The early edge is written as that hold (a length
 * curve from the note's start), because a share of the note with a floor and cap cannot follow the
 * eased middle.
 *
 * Tight and loose write the early edge as a share of the note with a cap (a quarter of the note,
 * at most 0.375 beat; three quarters, at most 1.125 beats) and close 0.15 or 0.45 beat late.
 */
export const RELEASE_CURVES: Readonly<Record<Tightness, CreditCurve>> = {
  tight: {
    unit: 'beats',
    plateau: [-0.25, 0.15],
    below: YELLOW_ONWARD,
    above: YELLOW_ONWARD,
    sides: { below: { unit: 'proportion', max: { amount: 0.375, unit: 'beats' } } },
  },
  standard: {
    unit: 'beats',
    plateau: [0, 0.3],
    below: YELLOW_ONWARD,
    above: YELLOW_ONWARD,
    sides: {
      below: {
        unit: 'beats',
        edgeByLength: {
          from: 'start',
          points: [
            { length: 0, value: 0, shape: 'linear' },
            { length: 1, value: 0.5, shape: 'linear' },
            { length: 2, value: 1.25, shape: 'curve', exponent: 0.6 },
          ],
        },
      },
    },
  },
  loose: {
    unit: 'beats',
    plateau: [-0.75, 0.45],
    below: YELLOW_ONWARD,
    above: YELLOW_ONWARD,
    sides: { below: { unit: 'proportion', max: { amount: 1.125, unit: 'beats' } } },
  },
};
